import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Locate } from "lucide-react";
import { api } from "@/lib/api";
import { useGoogleMaps } from "@/lib/maps";

// Phase 44.05 — Module-level flag: una volta che la chiave browser ritorna
// REQUEST_DENIED su Directions (referrer restrictions vs Directions API),
// tutte le successive richieste saltano direttamente al proxy server-side.
// Riduce log warning + round-trip wasted + tempo di rendering polyline.
let _clientDirectionsBlocked = false;

/**
 * OrderTrackingMap — Phase 43.7
 *
 * Visualizza pickup, delivery, marker driver + polyline reale stradale.
 * Novità:
 *  - Polyline split: nero (planned) + blu (percorso già fatto)
 *  - Zoom intelligente: focus su (driver + prossima tappa) in base alla `phase`
 *  - Pulsante mirino "ricentra" sulla mappa
 *
 * Fallback "stylized SVG" map quando Google Maps non è disponibile.
 *
 * Props:
 *   pickup: {lat, lng} | null
 *   delivery: {lat, lng} | null
 *   driver: {lat, lng, heading?} | null
 *   trail: [{lat, lng, ts}] — posizioni driver storiche (per fallback split)
 *   phase: "to_pickup" | "to_delivery" | null
 *       Quando driver attivo: "to_pickup" zoom su driver↔pickup;
 *       "to_delivery" zoom su driver↔delivery; altrimenti fit su tutto.
 *   showCompleted: bool (default false) — mostra anche la polyline blu del
 *       percorso già fatto. Off di default per ridurre rumore visivo: la
 *       linea nera resta sempre intera pickup → delivery, la arancio segna
 *       la prossima tappa.
 *   className
 */
export default function OrderTrackingMap({
  pickup, delivery, driver,
  trail = [],
  phase = null,
  showCompleted = false,
  className = "h-72 sm:h-96 w-full rounded-2xl overflow-hidden",
}) {
  const { ready, google, config } = useGoogleMaps();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({ pickup: null, delivery: null, driver: null });
  const trailPolylineRef = useRef(null);
  const plannedPolyRef = useRef(null);    // nero — tratta rimanente
  const completedPolyRef = useRef(null);  // blu  — tratta percorsa
  const nextLegPolyRef = useRef(null);    // amber — driver → prossima tappa (Phase 43.14)
  const nextLegKeyRef = useRef(null);     // cache key per evitare richieste continue
  const nextLegStrategyRef = useRef("road");  // "road" | "straight" — fallback indicator
  const overviewPathRef = useRef(null);   // cache full overview_path
  const routeKeyRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const userInteractedRef = useRef(false);
  // Phase 43.9 — flag UI: indica se il percorso visualizzato è quello reale
  // di Google Directions ("road") o un fallback geodesic ("straight").
  const [routeStrategy, setRouteStrategy] = useState("road");

  // ── Init mappa ───────────────────────────────────────────────────────────
  // Phase 44.11 — Deps ristrette a [ready, google] per evitare run inutili
  // ad ogni cambio reference di pickup/delivery/config (causa wasted renders).
  // Il fit iniziale viene poi gestito dall'effetto markers/polyline → smartFit.
  useEffect(() => {
    if (!ready || !google || !containerRef.current) return;
    if (mapRef.current) return;
    const center = pickup || delivery || config?.service_area?.center || { lat: 46.171, lng: 8.7935 };
    mapRef.current = new google.maps.Map(containerRef.current, {
      center,
      zoom: 13,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "greedy",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
    // L'utente ha pannato/zoomato → smettiamo di auto-fittare
    const onUser = () => { userInteractedRef.current = true; };
    mapRef.current.addListener("dragstart", onUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, google]);

  // ── Split helpers ────────────────────────────────────────────────────────
  const splitPathAtClosest = (path, point) => {
    // path: array di google.maps.LatLng | LatLngLiteral
    // point: {lat, lng}
    // Ritorna { completed: LatLng[], remaining: LatLng[] }
    if (!path || path.length < 2 || !point) return null;
    const sphere = google.maps.geometry?.spherical;
    if (!sphere) return null;
    const pt = new google.maps.LatLng(point.lat, point.lng);
    let bestIdx = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < path.length; i++) {
      const seg = path[i];
      const segLL = seg instanceof google.maps.LatLng ? seg : new google.maps.LatLng(seg.lat, seg.lng);
      const d = sphere.computeDistanceBetween(pt, segLL);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const completed = path.slice(0, bestIdx + 1).map((p) =>
      p instanceof google.maps.LatLng ? p : new google.maps.LatLng(p.lat, p.lng)
    );
    completed.push(pt);
    const remaining = [pt, ...path.slice(bestIdx + 1).map((p) =>
      p instanceof google.maps.LatLng ? p : new google.maps.LatLng(p.lat, p.lng)
    )];
    return { completed, remaining, snapDistanceM: bestDist };
  };

  // ── Smart fit / recenter ─────────────────────────────────────────────────
  const smartFit = (force = false) => {
    const map = mapRef.current;
    if (!map || !google) return;
    if (!force && userInteractedRef.current) return;

    const bounds = new google.maps.LatLngBounds();
    let any = false;
    const include = (p) => { if (p) { bounds.extend(p); any = true; } };

    if (driver && phase === "to_pickup" && pickup) {
      include(driver); include(pickup);
    } else if (driver && phase === "to_delivery" && delivery) {
      include(driver); include(delivery);
    } else {
      include(pickup); include(delivery); include(driver);
    }
    if (any && !bounds.isEmpty()) {
      // Padding generoso per visibilità marker + UI
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    }
  };

  const handleRecenter = () => {
    userInteractedRef.current = false;
    smartFit(true);
  };

  // ── Marker / polyline render ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !google) return;
    const map = mapRef.current;

    // Pickup marker (blue)
    if (pickup) {
      if (!markersRef.current.pickup) {
        markersRef.current.pickup = new google.maps.Marker({
          position: pickup, map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 9, fillColor: "#0052FF", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3,
          },
          title: "Ritiro",
          zIndex: 5,
        });
      } else {
        markersRef.current.pickup.setPosition(pickup);
      }
    }
    // Delivery marker (red)
    if (delivery) {
      if (!markersRef.current.delivery) {
        markersRef.current.delivery = new google.maps.Marker({
          position: delivery, map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 9, fillColor: "#DC2626", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3,
          },
          title: "Consegna",
          zIndex: 5,
        });
      } else {
        markersRef.current.delivery.setPosition(delivery);
      }
    }
    // Driver marker (animated arrow)
    if (driver) {
      const icon = {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 6, fillColor: "#09090B", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
        rotation: driver.heading ?? 0,
      };
      if (!markersRef.current.driver) {
        markersRef.current.driver = new google.maps.Marker({
          position: driver, map, icon, title: "Driver", zIndex: 10,
        });
      } else {
        markersRef.current.driver.setPosition(driver);
        markersRef.current.driver.setIcon(icon);
      }
    } else if (markersRef.current.driver) {
      markersRef.current.driver.setMap(null);
      markersRef.current.driver = null;
    }

    // Polyline percorso reale (Google Directions) — pickup → delivery
    if (pickup && delivery) {
      const key = `${pickup.lat},${pickup.lng}|${delivery.lat},${delivery.lng}`;
      if (routeKeyRef.current !== key) {
        routeKeyRef.current = key;
        overviewPathRef.current = null;
        if (!directionsServiceRef.current) {
          try { directionsServiceRef.current = new google.maps.DirectionsService(); } catch { /* */ }
        }
        const drawFallback = () => {
          overviewPathRef.current = [
            new google.maps.LatLng(pickup.lat, pickup.lng),
            new google.maps.LatLng(delivery.lat, delivery.lng),
          ];
          setRouteStrategy("straight");
          renderSplit();
        };
        // Phase 43.10 — Fallback automatico al proxy backend quando il client
        // DirectionsService ritorna REQUEST_DENIED o errore non-OK.
        const tryBackendProxy = async () => {
          try {
            const { data } = await api.post("/maps/directions", { pickup, delivery });
            if (data?.found && Array.isArray(data.points) && data.points.length >= 2) {
              overviewPathRef.current = data.points.map(
                (pt) => new google.maps.LatLng(pt.lat, pt.lng)
              );
              setRouteStrategy("road");
              renderSplit();
              return;
            }
            // eslint-disable-next-line no-console
            console.warn("[OrderTrackingMap] Backend Directions proxy returned not-found");
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[OrderTrackingMap] Backend Directions proxy failed:", e?.message);
          }
          drawFallback();
        };
        if (directionsServiceRef.current && !_clientDirectionsBlocked) {
          directionsServiceRef.current.route(
            { origin: pickup, destination: delivery, travelMode: google.maps.TravelMode.DRIVING },
            (result, status) => {
              if (status === "OK" && result?.routes?.[0]?.overview_path) {
                overviewPathRef.current = result.routes[0].overview_path;
                setRouteStrategy("road");
                renderSplit();
              } else {
                // REQUEST_DENIED → la chiave browser ha referrer restrictions
                // incompatibili con Directions. Segna globalmente per non
                // ritentare a ogni render.
                if (status === "REQUEST_DENIED") {
                  _clientDirectionsBlocked = true;
                  // eslint-disable-next-line no-console
                  console.info("[OrderTrackingMap] Client Directions disabled (referrer restriction) — using backend proxy.");
                }
                tryBackendProxy();
              }
            },
          );
        } else {
          tryBackendProxy();
        }
      } else {
        renderSplit();
      }
    }

    // Trail polyline (storico passi driver) - solo se non abbiamo split blu
    // Phase 43.56 — Trail GPS = verità principale, blu dominante anche senza split
    if (trail && trail.length > 1 && !driver) {
      const path = trail.map((p) => ({ lat: p.lat, lng: p.lng }));
      if (!trailPolylineRef.current) {
        trailPolylineRef.current = new google.maps.Polyline({
          path, geodesic: true,
          strokeColor: "#0052FF", strokeOpacity: 1, strokeWeight: 6,
          map, zIndex: 4,
        });
      } else {
        trailPolylineRef.current.setPath(path);
      }
    } else if (trailPolylineRef.current) {
      trailPolylineRef.current.setMap(null);
      trailPolylineRef.current = null;
    }

    // Phase 43.14 — Tratta arancio "driver → prossima tappa" (in cima al planned)
    renderNextLeg();

    smartFit(false);

    function plannedPolylineOptions(path) {
      // Phase 43.58 — Itinerario previsto = SCHELETRO ordine, sempre visibile
      // come riferimento generale (pickup → delivery). NERO solido per la route
      // reale di Google, tratteggiato nero per il fallback "straight" (quando
      // Google Directions non ha risposto).
      const isFallback = routeStrategy === "straight";
      if (isFallback) {
        return {
          path, geodesic: true,
          strokeOpacity: 0,
          icons: [{
            icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: "#09090B", strokeWeight: 2, scale: 3 },
            offset: "0",
            repeat: "12px",
          }],
          map: mapRef.current, zIndex: 2,
        };
      }
      return {
        path, geodesic: false,
        // Itinerario previsto solid nero — scheletro ordine
        strokeColor: "#09090B", strokeOpacity: 0.85, strokeWeight: 4,
        map: mapRef.current, zIndex: 2,
      };
    }

    function renderSplit() {
      const overview = overviewPathRef.current;
      if (!overview) return;
      // Linea nera/grigia: SEMPRE l'intero percorso pickup → delivery, anche se
      // il driver è in viaggio. Mostra a colpo d'occhio il percorso complessivo
      // dell'ordine. Lo split blu (percorso fatto) è opt-in via `showCompleted`.
      if (!showCompleted || !driver) {
        if (completedPolyRef.current) { completedPolyRef.current.setMap(null); completedPolyRef.current = null; }
        if (plannedPolyRef.current) { plannedPolyRef.current.setMap(null); }
        plannedPolyRef.current = new google.maps.Polyline(plannedPolylineOptions(overview));
        return;
      }

      // showCompleted=true → split su closest point
      const split = splitPathAtClosest(overview, driver);
      if (!split) {
        if (plannedPolyRef.current) { plannedPolyRef.current.setMap(null); }
        plannedPolyRef.current = new google.maps.Polyline(plannedPolylineOptions(overview));
        return;
      }
      // Phase 43.56 — Tracking reale GPS = verità principale, blu dominante
      // (sopra al percorso stimato grigio). z-index 4 = sopra planned (z=1).
      if (!completedPolyRef.current) {
        completedPolyRef.current = new google.maps.Polyline({
          path: split.completed, geodesic: false,
          strokeColor: "#0052FF", strokeOpacity: 0.55, strokeWeight: 4,
          map, zIndex: 3,
        });
      } else {
        completedPolyRef.current.setPath(split.completed);
        completedPolyRef.current.setMap(map);
      }
      // Remaining (nero solid o grigio tratteggiato in fallback)
      if (plannedPolyRef.current) { plannedPolyRef.current.setMap(null); }
      plannedPolyRef.current = new google.maps.Polyline(plannedPolylineOptions(split.remaining));
    }

    // Phase 43.14 — render della tratta arancio driver → prossima tappa
    function renderNextLeg() {
      // Determina la prossima tappa in base alla phase
      let target = null;
      if (phase === "to_pickup" && pickup) target = pickup;
      else if (phase === "to_delivery" && delivery) target = delivery;

      // Niente driver / niente phase utile → rimuovi il leg arancio
      if (!driver || !target) {
        if (nextLegPolyRef.current) {
          nextLegPolyRef.current.setMap(null);
          nextLegPolyRef.current = null;
          nextLegKeyRef.current = null;
        }
        return;
      }

      // Cache key: arrotondiamo a ~50m (4° decimale lat ≈ 11m) per evitare
      // richieste continue al cambio GPS. Si re-fetch quando il driver
      // si è spostato di un quantum o quando cambia la phase/tappa.
      const round = (n) => Math.round(n * 1000) / 1000;  // ~110m
      const key = `${phase}|${round(driver.lat)},${round(driver.lng)}|${round(target.lat)},${round(target.lng)}`;
      if (nextLegKeyRef.current === key && nextLegPolyRef.current) {
        return;  // già renderizzato, nessun bisogno di rifare la fetch
      }
      nextLegKeyRef.current = key;

      const setNextLegPath = (path, strategy) => {
        nextLegStrategyRef.current = strategy;
        if (nextLegPolyRef.current) {
          nextLegPolyRef.current.setMap(null);
        }
        // Phase 43.58 — Colore dinamico in base alla phase:
        //   to_pickup  → ARANCIONE #F59E0B (navigazione live al ritiro)
        //   to_delivery → AZZURRO #0052FF (navigazione live alla consegna)
        const liveColor = phase === "to_delivery" ? "#0052FF" : "#F59E0B";
        const baseOpts = {
          path, geodesic: false, map: mapRef.current, zIndex: 6,  // sopra planned (z=2) e completed (z=3)
        };
        if (strategy === "straight") {
          nextLegPolyRef.current = new google.maps.Polyline({
            ...baseOpts,
            strokeOpacity: 0,
            icons: [{
              icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: liveColor, strokeWeight: 4, scale: 3 },
              offset: "0",
              repeat: "10px",
            }],
          });
        } else {
          nextLegPolyRef.current = new google.maps.Polyline({
            ...baseOpts,
            strokeColor: liveColor, strokeOpacity: 1, strokeWeight: 6,
          });
        }
      };

      // 1) prova client DirectionsService
      const drawStraight = () => setNextLegPath(
        [new google.maps.LatLng(driver.lat, driver.lng), new google.maps.LatLng(target.lat, target.lng)],
        "straight",
      );
      const tryBackendProxy = async () => {
        try {
          const { data } = await api.post("/maps/directions", { pickup: driver, delivery: target });
          if (data?.found && Array.isArray(data.points) && data.points.length >= 2) {
            const path = data.points.map((pt) => new google.maps.LatLng(pt.lat, pt.lng));
            // Verifica che la cache key sia ancora valida (driver potrebbe essersi mosso nel frattempo)
            if (nextLegKeyRef.current === key) setNextLegPath(path, "road");
            return;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[OrderTrackingMap] next-leg proxy failed:", e?.message);
        }
        if (nextLegKeyRef.current === key) drawStraight();
      };
      if (directionsServiceRef.current && !_clientDirectionsBlocked) {
        directionsServiceRef.current.route(
          { origin: driver, destination: target, travelMode: google.maps.TravelMode.DRIVING },
          (result, status) => {
            // Cache key invalidata da successivi update → ignora il risultato stale
            if (nextLegKeyRef.current !== key) return;
            if (status === "OK" && result?.routes?.[0]?.overview_path) {
              setNextLegPath(result.routes[0].overview_path, "road");
            } else {
              if (status === "REQUEST_DENIED") _clientDirectionsBlocked = true;
              tryBackendProxy();
            }
          },
        );
      } else {
        tryBackendProxy();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google, pickup, delivery, driver, trail, phase, routeStrategy, showCompleted]);

  // Quando cambia la phase → re-centriamo (rispettando interazione utente)
  useEffect(() => {
    if (!mapRef.current) return;
    // forziamo solo se non c'è interazione: smartFit normale
    smartFit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Phase 43.14 — cleanup on unmount: rimuovi la polyline next-leg
  useEffect(() => {
    return () => {
      if (nextLegPolyRef.current) {
        try { nextLegPolyRef.current.setMap(null); } catch { /* */ }
        nextLegPolyRef.current = null;
      }
    };
  }, []);

  // ── Fallback "stylized" map ──────────────────────────────────────────────
  if (!ready) {
    return (
      <div className={`${className} bg-gradient-to-br from-slate-100 to-slate-200 relative`} data-testid="tracking-map-fallback">
        <div className="absolute inset-0 map-bg opacity-60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-md text-center max-w-xs">
            <p className="text-xs text-dapnow-muted">Mappa interattiva non disponibile</p>
            <p className="text-xs text-dapnow-muted mt-1">Puoi seguire gli aggiornamenti dello stato della consegna in questa pagina.</p>
          </div>
        </div>
        {pickup && <span className="absolute top-1/3 left-12 h-3 w-3 rounded-full bg-dapnow-blue ring-8 ring-dapnow-blue/30" />}
        {delivery && <span className="absolute bottom-12 right-12 h-3 w-3 rounded-full bg-dapnow-red ring-8 ring-red-500/30" />}
        {driver && <span className="absolute top-1/2 left-1/2 h-4 w-4 rounded-full bg-dapnow-ink ring-8 ring-dapnow-ink/20 animate-pulse" />}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={containerRef} className="absolute inset-0" data-testid="tracking-map" />
      {/* Legenda compatta — Phase 43.58:
            NERO = itinerario previsto (scheletro ordine, riferimento)
            ARANCIONE = navigazione live al ritiro (phase=to_pickup)
            AZZURRO = navigazione live alla consegna (phase=to_delivery) */}
      {driver && phase && (
        <div
          className="absolute bottom-3 left-3 z-10 rounded-xl bg-white/95 backdrop-blur shadow-md ring-1 ring-black/10 px-2.5 py-1.5 text-[10px] font-semibold text-dapnow-ink space-y-1"
          data-testid="tracking-map-legend"
        >
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-4 rounded-full" style={{
              background: phase === "to_delivery" ? "#0052FF" : "#F59E0B",
            }} />
            <span>
              Navigazione live · {phase === "to_pickup" ? "verso ritiro" : "verso consegna"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-4 rounded-full" style={{ background: "#09090B" }} />
            <span>Itinerario previsto</span>
          </div>
        </div>
      )}
      {/* Badge discreto quando il percorso è fallback (linea geodesic tratteggiata) */}
      {routeStrategy === "straight" && pickup && delivery && (
        <div
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur shadow-md ring-1 ring-black/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-700"
          data-testid="tracking-map-fallback-badge"
        >
          <AlertTriangle className="h-3 w-3" />
          Percorso stradale non disponibile
        </div>
      )}
      {/* Pulsante mirino — top-left (Google Maps mette zoom controls in basso a destra di default) */}
      <button
        type="button"
        onClick={handleRecenter}
        data-testid="tracking-map-recenter"
        aria-label="Ricentra mappa"
        className="absolute top-3 left-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-black/10 text-dapnow-ink hover:bg-dapnow-bg active:scale-95 transition"
      >
        <Locate className="h-4 w-4" />
      </button>
    </div>
  );
}
